import React, { useState } from "react";
import { Box, Modal, Typography, IconButton, Button, CircularProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Fileuploadrequestcall } from "../../request/Fileuploadrequest";
import placeholder_img from "../../assets/images/placeholder_img.jpg";
import { ANALYZE_IMAGE } from "../../constant/Apipath";

export default function BILens({ open, onClose }) {
  const [dragActive, setDragActive] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resultData, setResultData] = useState(null);
  const navigate = useNavigate();
  const { loginData } = useSelector((state) => state?.main);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      setSelectedImage(e.target.result);
      processImage(file);
    };
    reader.readAsDataURL(file);
  };

  const processImage = async (file) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      const response = await Fileuploadrequestcall(ANALYZE_IMAGE, formData, loginData?.accessToken);
      if (response?.data?.status && response?.data?.data) {
        const d = response.data.data;
        if (d.type === "location") {
          const locationDetails = d.locationDetails || {};
          setResultData({
            type: "location",
            placeName: locationDetails.placeName,
            latitude: locationDetails.latitude,
            longitude: locationDetails.longitude,
            address: (() => {
              const parts = [
                locationDetails?.address,
                locationDetails?.city,
                locationDetails?.province,
                locationDetails?.country
              ].filter((value) => value !== null && value !== undefined && value !== "");
              return parts.length > 0 ? parts.join(", ") : "";
            })(),
            items: (d.sites || []).map((s) => ({
              id: s.siteId || s.id,
              name: s.siteName || s.name || "Unnamed Site",
              description:
                s.address ||
                (s.createdByUser ? `Created by: ${s.createdByUser}` : "No details provided"),
              distance: s.distanceKm,
              image:
                s?.siteImageUrl === null || s?.siteImageUrl === ""
                  ? placeholder_img
                  : s?.siteImageUrl,
              route: "/sites",
              rawSite: s
            }))
          });
        } else if (d.type === "product") {
          const productDetails = d.productDetails || {};
          setResultData({
            type: "product",
            placeName: productDetails?.placeName,
            latitude: productDetails?.latitude,
            longitude: productDetails?.longitude,
            address: productDetails?.address || "",
            productName: productDetails?.productName || "Detected Product",
            productDescription: productDetails?.description || "No description available",
            productBrand: productDetails?.brand || "",
            productType: productDetails?.productType || "",
            productFeatures: Array.isArray(productDetails?.features)
              ? productDetails?.features
              : [],
            items: (d.products || []).map((p) => ({
              id: p.productId || p.id,
              name: p.name || p.productName || "Unnamed Product",
              description: p.info || p.description || "No details provided",
              distance: p.distance,
              image:
                p?.pictureUrl === null || p?.pictureUrl === "" ? placeholder_img : p?.pictureUrl,
              route: "/product/" + (p?.id || p?.productId),
              rawProduct: p
            }))
          });
        } else {
          setResultData({ type: "unknown", items: [] });
        }
      } else {
        setResultData({ type: "unknown", items: [] });
      }
    } catch (error) {
      console.error("Error detecting image location:", error);
      setResultData({ type: "site", items: [] });
    } finally {
      setLoading(false);
    }
  };

  const handleItemClick = (item) => {
    onClose();
    if (item.rawSite) {
      navigate(item.route, { state: { site: item.rawSite } });
    } else {
      navigate(item.route);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "95%", sm: 700 },
          bgcolor: "background.paper",
          borderRadius: 3,
          boxShadow: "0 24px 48px rgba(0,0,0,0.2)",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden"
        }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: "16px 24px",
            background: "linear-gradient(90deg, #002045 0%, #003473 100%)",
            color: "#fff"
          }}>
          <Typography
            variant="h6"
            component="h2"
            sx={{
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              letterSpacing: "0.5px"
            }}>
            <CloudUploadIcon /> BI Lens Analysis
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              color: "rgba(255,255,255,0.8)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.1)", color: "#fff" }
            }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 4, overflow: "auto", flex: 1 }}>
          {!selectedImage ? (
            <Box
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              sx={{
                border: "2px dashed",
                borderColor: dragActive ? "#3b82f6" : "#cbd5e1",
                borderRadius: 4,
                p: 8,
                textAlign: "center",
                bgcolor: dragActive ? "#f0f9ff" : "#f8fafc",
                cursor: "pointer",
                position: "relative",
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: dragActive ? "#3b82f6" : "#94a3b8",
                  bgcolor: dragActive ? "#f0f9ff" : "#f1f5f9"
                }
              }}>
              <input
                type="file"
                accept="image/*"
                onChange={handleChange}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  opacity: 0,
                  cursor: "pointer",
                  zIndex: 2
                }}
              />
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  bgcolor: "#e2e8f0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto",
                  mb: 3,
                  color: "#475569",
                  transition: "all 0.2s ease",
                  ...(dragActive && {
                    bgcolor: "#dbeafe",
                    color: "#2563eb",
                    transform: "scale(1.1)"
                  })
                }}>
                <CloudUploadIcon sx={{ fontSize: 40 }} />
              </Box>
              <Typography variant="h6" sx={{ color: "#1e293b", fontWeight: 600, mb: 1 }}>
                {dragActive ? "Drop your image here!" : "Click or drag to upload"}
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b" }}>
                Upload any image to identify landmarks instantly.
              </Typography>
            </Box>
          ) : (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 2,
                  p: 1.5,
                  bgcolor: "#f8fafc",
                  borderRadius: 3,
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
                }}>
                <Box sx={{ position: "relative", width: 56, height: 56, marginRight: 2 }}>
                  <img
                    src={selectedImage}
                    alt="Uploaded"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: "8px",
                      border: "2px solid #fff",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: -4,
                      right: -4,
                      bgcolor: "#10b981",
                      width: 14,
                      height: 14,
                      borderRadius: "50%",
                      border: "2px solid #fff"
                    }}
                  />
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b" }}>
                    Image Captured
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 500 }}>
                    {loading ? "Analyzing..." : "Analysis complete"}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setSelectedImage(null);
                    setResultData(null);
                  }}
                  startIcon={<CloudUploadIcon />}
                  sx={{
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 600,
                    border: "1px solid #cbd5e1",
                    color: "#475569",
                    "&:hover": { bgcolor: "#f1f5f9", borderColor: "#94a3b8" }
                  }}>
                  Upload New
                </Button>
              </Box>

              {loading ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 8
                  }}>
                  <Box
                    sx={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}>
                    <CircularProgress size={64} thickness={2} sx={{ color: "#e2e8f0" }} />
                    <CircularProgress
                      size={64}
                      thickness={4}
                      sx={{
                        color: "primary.main",
                        position: "absolute",
                        animationDuration: "1.2s"
                      }}
                      disableShrink
                    />
                    <CameraAltIcon
                      sx={{ position: "absolute", color: "primary.main", fontSize: 24 }}
                    />
                  </Box>
                  <Typography sx={{ mt: 3, color: "#334155", fontWeight: 600, fontSize: "16px" }}>
                    Scanning Image...
                  </Typography>
                  <Typography sx={{ mt: 0.5, color: "#64748b", fontSize: "14px" }}>
                    Identifying products and landmarks
                  </Typography>
                </Box>
              ) : resultData ? (
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: resultData.type === "product" ? "start" : "center",
                      mb: 2,
                      flexDirection: resultData.type === "product" ? "column" : "row",
                      gap: resultData.type === "product" ? "10px" : 0
                    }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "bold",
                        color: "text.primary",
                        borderBottom: "2px solid",
                        borderColor: "primary.main",
                        pb: 0.5
                      }}>
                      Identified {resultData.type === "location" ? "Locations & Sites" : "Products"}
                    </Typography>
                    {resultData.type === "location" ? (
                      <Typography variant="body2" sx={{ color: "primary.main", fontWeight: 600 }}>
                        {resultData?.items?.length} nearby site
                        {resultData?.items?.length !== 1 ? "s" : ""} found
                      </Typography>
                    ) : resultData.type === "product" ? (
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: "linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)",
                          border: "1px solid #dbeafe",
                          boxShadow: "0 4px 12px rgba(15, 23, 42, 0.06)",
                          width: "100%"
                        }}>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 700, color: "#111827", mb: 0.5 }}>
                          Product Name : {resultData?.productName}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#475569", lineHeight: 1.6 }}>
                          {resultData?.productDescription}
                        </Typography>
                        {(resultData?.productBrand || resultData?.productType) && (
                          <Box sx={{ mt: 1.25, display: "flex", flexWrap: "wrap", gap: 1 }}>
                            {resultData?.productBrand && (
                              <Typography
                                variant="caption"
                                sx={{
                                  px: 1,
                                  py: 0.4,
                                  borderRadius: 999,
                                  bgcolor: "#e2e8f0",
                                  color: "#334155"
                                }}>
                                {resultData?.productBrand}
                              </Typography>
                            )}
                            {resultData?.productType && (
                              <Typography
                                variant="caption"
                                sx={{
                                  px: 1,
                                  py: 0.4,
                                  borderRadius: 999,
                                  bgcolor: "#dbeafe",
                                  color: "#1d4ed8"
                                }}>
                                {resultData?.productType}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    ) : null}
                  </Box>

                  {resultData?.placeName && (
                    <Typography
                      variant="subtitle1"
                      sx={{ mb: 1, color: "text.secondary", fontWeight: "medium" }}>
                      <strong>Detected Area:</strong> {resultData?.placeName}
                    </Typography>
                  )}

                  <Typography
                    variant="subtitle1"
                    sx={{ mb: 1, color: "text.secondary", fontWeight: "medium" }}>
                    {resultData.type === "product" ? (
                      <>
                        <strong>Suggested Products :</strong>
                      </>
                    ) : (
                      <>
                        <strong>Address:</strong> {resultData?.address}
                      </>
                    )}
                  </Typography>

                  {resultData?.latitude && resultData?.longitude && (
                    <Box sx={{ mb: 2 }}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => {
                          const mapUrl = `https://www.google.com/maps?q=${resultData?.latitude},${resultData?.longitude}`;
                          window.open(mapUrl, "_blank");
                        }}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          bgcolor: "#4285f4",
                          color: "#fff",
                          "&:hover": { bgcolor: "#3367d6" },
                          boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                        }}>
                        📍 View on Google Maps
                      </Button>
                    </Box>
                  )}

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                      gap: 2
                    }}>
                    {resultData?.items?.map((item) => (
                      <Box
                        key={item?.id}
                        onClick={() => handleItemClick(item)}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          borderRadius: 1,
                          overflow: "hidden",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                          border: "1px solid",
                          borderColor: "grey.200",
                          cursor: "pointer",
                          bgcolor: "background.paper",
                          transition: "transform 0.1s, box-shadow 0.1s",
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
                          }
                        }}>
                        <img
                          src={item?.image}
                          alt={item?.name}
                          style={{ width: "100%", height: 140, objectFit: "cover" }}
                        />
                        <Box sx={{ p: 1.5 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: "bold", mb: 0.5, lineHeight: 1.2 }}>
                            {item?.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              mb: 0.5
                            }}>
                            {item?.description}
                          </Typography>
                          {item?.distance !== undefined && (
                            <Typography
                              variant="caption"
                              sx={{
                                display: "block",
                                mt: 1,
                                color: "primary.main",
                                fontWeight: "bold",
                                bgcolor: "#f1f5f9",
                                p: 0.5,
                                borderRadius: 1,
                                display: "inline-block"
                              }}>
                              {item?.distance} km away
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ) : null}
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  );
}
